import {
  type ElementType,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptionProps,
  ListboxOptions,
  ListboxProps,
} from "@headlessui/react";
import classNames from "classnames";

import SolidArrowIcon from "@/components/Icons/arrows/SolidArrowIcon";
import Placeholder, { Rect } from "@/components/Skeleton/Skeleton";

type DropdownItemProps = PropsWithChildren<
  Omit<ListboxOptionProps<ElementType>, "value">
>;

type DropdownV2Props<T> = ListboxProps & {
  className?: string;
  onToggleDropdown?: (isOpen: boolean) => void;
  trackClickEvent?: () => void;
  options: T[];
  preselectedOption?: T;
  onItemRender: (option: T, index?: number) => DropdownItemProps;
  label?: string;
  variant?: "default" | "small";
  loading?: boolean;
};

// Generic Dropdown component using HeadlessUI Listbox
// using `T extends string | number` to enable bolding of the selected option
const DropdownV2 = <T extends string | number>({
  onToggleDropdown,
  trackClickEvent,
  preselectedOption,
  options,
  onItemRender,
  label,
  variant = "default",
  loading = false,
  ...props
}: DropdownV2Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<T>(
    preselectedOption || options[0],
  );

  useEffect(() => {
    if (preselectedOption) {
      setSelectedOption(preselectedOption);
    }
  }, [preselectedOption]);

  const toggleDropdown = (isOpen: boolean) => {
    setIsOpen(isOpen);
    onToggleDropdown?.(isOpen);
    if (isOpen) {
      trackClickEvent?.();
    }
  };

  const optionNodes = useMemo(
    () =>
      options.map((option, index) => {
        const { className, ...optionProps } = onItemRender(option, index) || {};

        return (
          <ListboxOption
            key={index}
            value={option}
            className={`rounded-sm p-1.5 aria-selected:font-bold hover:bg-light-grey text-xs ${className}`}
            {...optionProps}
          />
        );
      }),
    [options, onItemRender],
  );

  return (
    <Field
      className={classNames("flex gap-xs", {
        "text-xs": variant === "small",
      })}
    >
      {!loading ? (
        <>
          {label && <Label className="after:content-['_']">{label}</Label>}
          <Listbox
            value={selectedOption}
            onChange={(v) => setSelectedOption(v as T)}
            as="div"
            {...props}
          >
            <ListboxButton className="flex justify-between font-bold w-full">
              {onItemRender(selectedOption).children}{" "}
              {!props.disabled && (
                <SolidArrowIcon
                  className={classNames("transition -mt-[0.15rem]", {
                    "rotate-180": isOpen,
                  })}
                />
              )}
            </ListboxButton>
            <ListboxOptions
              anchor="bottom"
              className="cursor-pointer p-sm shadow-low bg-white z-200 flex flex-col gap-xs rounded-xl"
              // HeadlessUI does not provide a way to know if the dropdown is open
              // solution taken from: https://github.com/tailwindlabs/headlessui/discussions/2593
              ref={(node) => toggleDropdown(!!node)}
            >
              {optionNodes}
            </ListboxOptions>
          </Listbox>
        </>
      ) : (
        <Placeholder
          {...(variant === "small"
            ? { height: "22", width: "115" }
            : { height: "24", width: "143" })}
          aria-busy="true"
          aria-live="polite"
          aria-label={`loading ${label ? label + " dropdown..." : "dropdown..."}`}
        >
          <Rect height="100%" width="100%" />
        </Placeholder>
      )}
      {/* This is due to HeadlessUI adding these styles when the dropdown is open */}
      {/* Github Issue: https://github.com/tailwindlabs/headlessui/issues/2948 */}
      {isOpen && (
        <style global>
          {`
          html {
            padding-right: 0 !important;
            overflow: initial !important;
          }
        `}
        </style>
      )}
    </Field>
  );
};

export default DropdownV2;
